<script setup lang="ts">
import { IotDeviceStateOfUse, QaEquipmentSubCategory } from '@/api/generator';
import { computed, toRef } from 'vue';
import DeviceBatteryState from './DeviceBatteryState.vue';
import DeviceConnectedArray from './DeviceConnectedArray.vue';
import DeviceConnectionState from './DeviceConnectionState.vue';
import { useDeviceStatusSignalR } from './utils/useDeviceStatusSignalR';

const props = defineProps<{
  deviceId?: string;
  orientation?: 'horizontal' | 'vertical';
  showConnectedArray?: boolean;
  subCategory?: string;
}>();

const deviceState = useDeviceStatusSignalR(toRef(() => props.deviceId));

const showBattery = computed(() => props.subCategory !== QaEquipmentSubCategory.Interface);

// The connected-array info is only current while the device is communicating;
// Offline/Unknown would show the last-known array of a device that isn't there.
const deviceCommunicating = computed(
  () =>
    deviceState.value?.state !== IotDeviceStateOfUse.Offline &&
    deviceState.value?.state !== IotDeviceStateOfUse.Unknown,
);
</script>

<template>
  <span
    v-if="deviceState?.state"
    class="device-state d-flex align-content-center justify-content-center align-items-center ga-4"
    :class="{
      'flex-column': orientation === 'vertical',
      // Wraps so the connected-array chip drops to a second line in narrow
      // hosts (table column, tile) instead of overflowing.
      'flex-row flex-wrap': orientation === 'horizontal',
    }"
  >
    <DeviceConnectionState :value="deviceState?.state" />
    <DeviceBatteryState v-if="showBattery" :value="deviceState?.batteryInformation" />
    <DeviceConnectedArray
      v-if="showConnectedArray && deviceCommunicating"
      :value="deviceState?.deviceInformation"
    />
  </span>
</template>
